"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.refetchWhenExpires = exports.getTimeUntilExpiration = exports.getLocalToken = exports.fetchToken = exports.clearLocalToken = void 0;
var _regenerator = _interopRequireDefault(require("@babel/runtime/regenerator"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _identity = _interopRequireDefault(require("lodash/identity"));
var _jwtDecode = _interopRequireDefault(require("jwt-decode"));
var _luxon = require("luxon");
var _jsTools = require("@airportlabs/js-tools");
var REFETCH_BUFFER_SECONDS = 20;
var MAX_TIMEOUT_DELAY = 2147483647;

// Token renewal timer management
var timerStorage = {
  _getStorageKey: function _getStorageKey(tokenType) {
    return "".concat(tokenType, "_TIMER_ID");
  },
  get: function get(tokenType) {
    return localStorage.getItem(timerStorage._getStorageKey(tokenType));
  },
  save: function save(tokenType, timerId) {
    return localStorage.setItem(timerStorage._getStorageKey(tokenType), timerId);
  },
  remove: function remove(tokenType) {
    return localStorage.removeItem(timerStorage._getStorageKey(tokenType));
  },
  clear: function clear(tokenType) {
    clearTimeout(timerStorage.get(tokenType));
    timerStorage.remove(tokenType);
  }
};

/**
 * Deletes a token of a specific type from storage and  its auto-renewal timer.
 *
 * Example
 * - input: ACCESS_TOKEN
 * - output: undefined, ACCESS_TOKEN and ACCESS_TOKEN_TIMER_ID are removed from storage
 *
 * @param {String<ACCESS_TOKEN|REFRESH_TOKEN>} tokenType The token type.
 */
var clearLocalToken = exports.clearLocalToken = function clearLocalToken(tokenType) {
  localStorage.removeItem(tokenType);
  timerStorage.clear(tokenType);
};

/**
 * Returns the token of a specific type.
 *
 * If the token is expired, the token storage and its renewal timer are cleared
 * (@see clearLocalToken).
 *
 * @param {string<ACCESS_TOKEN|REFRESH_TOKEN>} tokenType The token type.
 * @return {(string|undefined)} The token if a valid token is found
 */
var getLocalToken = exports.getLocalToken = function getLocalToken(tokenType) {
  var token = localStorage.getItem(tokenType);
  var timeUntilExpiration = getTimeUntilExpiration(token);
  if (token && timeUntilExpiration <= 0) {
    _jsTools.authConsole.log("Removing expired ".concat(tokenType, " token \"").concat(token, "\" from storage because it expired ").concat(timeUntilExpiration * -1, "ms ago."));
    clearLocalToken(tokenType);
    return;
  }
  return token;
};

/**
 * Calculates the time (by default in milliseconds) until a specific token expires.
 *
 * @param {string} token The token.
 * @param {string} unit The time unit of the result
 * @see https://moment.github.io/luxon/docs/class/src/duration.js~Duration.html
 * @return {undefined}
 */
var getTimeUntilExpiration = exports.getTimeUntilExpiration = function getTimeUntilExpiration(token) {
  var unit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "milliseconds";
  if (!token) {
    return -1;
  }
  var decodedToken;
  try {
    decodedToken = (0, _jwtDecode["default"])(token);
  } catch (error) {
    console.error("Cannot decode JWT token", token, error);
    return 0;
  }
  if (!decodedToken.exp) {
    console.error("JWT token must contain 'exp' field, but it contains only", decodedToken);
    return 0;
  }
  var expirationDate = _luxon.DateTime.fromSeconds(decodedToken.exp).minus({
    seconds: REFETCH_BUFFER_SECONDS
  });
  var nowDate = _luxon.DateTime.fromMillis(Date.now());
  return expirationDate.diff(nowDate, unit).toObject()[unit];
};

/**
 * Looks at a token expiration time and sets a timer that refetches it
 * when it is about to expire.
 *
 * @param {string} token The token that is checked for the expiration time.
 * @param {Function} refreshTokenFn The function that fetches a new token when
 * this one is about to expire.
 * @param {string<ACCESS_TOKEN|REFRESH_TOKEN>} tokenType The token type. Used to
 * persist the timer id for cleanup.
 * @returns {undefined}
 */
var refetchWhenExpires = exports.refetchWhenExpires = function refetchWhenExpires(token, refreshTokenFn, tokenType) {
  var msUntilTokenExpiration = getTimeUntilExpiration(token, "milliseconds");
  var msUntilTokenRefetch = msUntilTokenExpiration - REFETCH_BUFFER_SECONDS * 1000;
  var timerId = setTimeout(function () {
    _jsTools.authConsole.logFn(function () {
      return "Timer ".concat(timerStorage.get(tokenType), " activated: Refetching ").concat(tokenType, " token that expires in ").concat(getTimeUntilExpiration(token, "seconds"), " seconds.");
    });
    refreshTokenFn();
  }, Math.min(msUntilTokenRefetch, MAX_TIMEOUT_DELAY));
  _jsTools.authConsole.log("Set a timer with id ".concat(timerId, " to refetch ").concat(tokenType, " token in ").concat(msUntilTokenRefetch / 1000, " seconds. Token expires in ").concat(msUntilTokenExpiration / 1000, " seconds."));
  clearTimeout(timerStorage.get(tokenType));
  timerStorage.save(tokenType, timerId);
};

/**
 * Executes a method that fetches a token, persists it, and if a renew function
 * is passed, it sets a timer that will execute it whenever the token is about
 * to expire.
 *
 * @param {Object} options The option object
 * @param {Function} options.fetchMethod The grpc service method that will fetch
 * the token.
 * @param {Object} options.input The grpc service request arguments.
 * @param {string<ACCESS_TOKEN|REFRESH_TOKEN>} options.tokenType The token type.
 * Used for localStorage persistence.
 * @param {Function} options.refreshTokenFn The fetch function that will be
 * executed when the token needs to be renewed. If omitted, the token won't
 * refresh.
 * @param {string} options._consoleGroupLabel A label for this fetching to be
 * used in debugging.
 * @returns {Promise} A promise that resolves with the newly fetched token.
 */
var fetchToken = exports.fetchToken = /*#__PURE__*/function () {
  var _ref2 = (0, _asyncToGenerator2["default"])( /*#__PURE__*/_regenerator["default"].mark(function _callee2(_ref) {
    var fetchMethod, input, tokenType, refreshTokenFn, _ref$onFetched, onFetched, _consoleGroupLabel;
    return _regenerator["default"].wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          fetchMethod = _ref.fetchMethod, input = _ref.input, tokenType = _ref.tokenType, refreshTokenFn = _ref.refreshTokenFn, _ref$onFetched = _ref.onFetched, onFetched = _ref$onFetched === void 0 ? _identity["default"] : _ref$onFetched, _consoleGroupLabel = _ref._consoleGroupLabel;
          return _context2.abrupt("return", new Promise( /*#__PURE__*/function () {
            var _ref3 = (0, _asyncToGenerator2["default"])( /*#__PURE__*/_regenerator["default"].mark(function _callee(resolve, reject) {
              var _yield$fetchMethod, data, error;
              return _regenerator["default"].wrap(function _callee$(_context) {
                while (1) switch (_context.prev = _context.next) {
                  case 0:
                    _context.next = 2;
                    return fetchMethod(input);
                  case 2:
                    _yield$fetchMethod = _context.sent;
                    data = _yield$fetchMethod.data;
                    error = _yield$fetchMethod.error;
                    if (!error) {
                      _context.next = 8;
                      break;
                    }
                    reject(error.message);
                    return _context.abrupt("return");
                  case 8:
                    localStorage.setItem(tokenType, data.token);
                    onFetched(data.token);
                    _jsTools.authConsole.groupCollapsed(_consoleGroupLabel || "fetching new ".concat(tokenType));
                    _jsTools.authConsole.log("Fetched ".concat(tokenType, " ").concat(data.token));
                    if (refreshTokenFn) {
                      refetchWhenExpires(data.token, refreshTokenFn, tokenType);
                    } else {
                      _jsTools.authConsole.log("No auto refresh for ".concat(tokenType, "."));
                    }
                    _jsTools.authConsole.groupEnd();
                    resolve(data.token);
                  case 15:
                  case "end":
                    return _context.stop();
                }
              }, _callee);
            }));
            return function (_x2, _x3) {
              return _ref3.apply(this, arguments);
            };
          }()));
        case 2:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return function fetchToken(_x) {
    return _ref2.apply(this, arguments);
  };
}();